<template>
  <BrowserDisplayOverlay v-if="isReady && display === 'overlay'" :content="highlightedContent">
    <template #default="{ elements, handleElementClick }">
      <BrowserCanvas @click="handleElementClick" :source="image" :elements="elements">
        <template #toolbar="{ handleAction }">
          <BrowserToolbar @input="handleAction" :displayMode="display" :tagMode="tag"></BrowserToolbar>
        </template>
      </BrowserCanvas>
    </template>
  </BrowserDisplayOverlay>
  <BrowserDisplayAside v-else-if="isReady && display === 'aside'" :content="highlightedContent">
    <template #canvas="{ elements, handleElementClick }">
      <BrowserCanvas @click="handleElementClick" :source="image" :elements="elements">
        <template #toolbar="{ handleAction }">
          <BrowserToolbar
            @input="handleAction"
            :show-text-visibility-toggle="false"
            :displayMode="display"
            :tagMode="tag"
          ></BrowserToolbar>
        </template>
      </BrowserCanvas>
    </template>
    <template #aside="{ elements, handleElementClick }">
      <BrowserContent :content="elements" @input="handleElementClick" />
    </template>
        <template #metadata="{ elements, handleElementClick }">
      <BrowserContentMetadata :content="elements" @input="handleElementClick" />
    </template>
  </BrowserDisplayAside>
  <b-loading v-else :active="!isReady" :is-full-page="false" style="z-index: 2"></b-loading>
</template>

<script>
import BrowserCanvas from "@/components/BrowserCanvas.vue";
import BrowserToolbar from "@/components/BrowserToolbar.vue";
import BrowserContent from "@/components/BrowserContent.vue";
import BrowserDisplayOverlay from "@/components/BrowserDisplayOverlay.vue";
import BrowserDisplayAside from "@/components/BrowserDisplayAside.vue";
import BrowserContentMetadata from "@/components/BrowserContentMetadata.vue"

import { createNamespacedHelpers } from "vuex";

import EventBus from "@/utils/event-bus.js";

const { mapState, mapActions, mapGetters } = createNamespacedHelpers("browser");

import { processSearchResults } from "@/utils/matches.js";

import { createMapper } from "@/utils/mapping.js";
import { createValidator } from "@/utils/validation.js";

const validator = createValidator("browser");
const mapper = createMapper("browser");

export default {
  components: {
    BrowserCanvas,
    BrowserToolbar,
    BrowserContent,
    BrowserContentMetadata,
    BrowserDisplayOverlay,
    BrowserDisplayAside
  },
  watch: {
    "$route.query": {
      handler(query) {
        const { display } = validator.clean(mapper.fromURL(query));
        const { tag } = validator.clean(mapper.fromURL(query));
        this.setDisplayMode(display);
        this.setTagMode(tag);
      },
      immediate: true
    },
    display: {
      handler(value) {
        if (value === validator.getDefault("display")) {
          const { display, ...query } = mapper.fromURL(this.$route.query);

          this.$router.push({
            ...this.$route,
            query: mapper.toURL(query)
          });
        } else {
          const query = mapper.toURL({ display: value });

          this.$router.push({
            ...this.$route,
            query: {
              ...this.$route.query,
              ...query
            }
          });
        }
      },
      immediate: false
    },
    tag: {
      handler(value) {
        if (value === validator.getDefault("tag")) {
          const { tag, ...query } = mapper.fromURL(this.$route.query);

          this.$router.push({
            ...this.$route,
            query: mapper.toURL(query)
          });
        } else {
          const query = mapper.toURL({ tag: value });

          this.$router.push({
            ...this.$route,
            query: {
              ...this.$route.query,
              ...query
            }
          });
        }
      },
      immediate: false
    }
  },
  computed: {
    ...mapGetters(["isTextVisible", "isReady"]),
    ...mapState({
      content: "pageContent",
      image: "imageUrl",
      results: "searchResults",
      display: "displayMode",
      tag: "tagMode"
    }),
    highlightedContent() {
      if (this.content === null) {
        return [];
      }

      try {
        return processSearchResults(this.results, this.content);
      } catch (err) {
        console.error(err);
        // no highlights
        return this.content;
      }
    }
  },
  created() {
    EventBus.$on("SET_DISPLAY_MODE", this.setDisplayMode);
    EventBus.$on("SET_TAG_MODE", this.setTagMode);
  },
  destroyed() {
    EventBus.$off("SET_DISPLAY_MODE", this.setDisplayMode);
    EventBus.$off("SET_TAG_MODE", this.setTagMode);
  },
  methods: {
    ...mapActions(["setDisplayMode", "setTagMode"])
  }
};
</script>
